import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const BG =
    "https://images.unsplash.com/photo-1617480173576-ceba9f423ab6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNvbmNyZXRlJTIwd2FsbCUyMHN0cmVldHdlYXIlMjB0ZXh0dXJlJTIwbmlnaHQlMjBiYXNrZXRiYWxsJTIwc3RyZWV0JTIwZm9vdGJhbGx8ZW58MHx8fGJsYWNrfDE3OTMyMTU2OTF8MA&ixlib=rb-4.1.0&q=85";

const CARDS = [
    {
        id: "futebol",
        title: "FUTEBOL",
        tag: "01 // STREET PITCH",
        img: "https://images.unsplash.com/photo-1652190364244-96ad73c21c4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodCUyMGxpZ2h0cyUyMHN0YWRpdW0lMjBuaWdodCUyMGxpZ2h0cyUyMHN0cmVldCUyMHNvY2NlciUyMGNvbmNyZXRlfGVufDB8fHxibGFja3wxNzkzMjE1Njk4fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "retro",
        title: "RETRÔ",
        tag: "02 // CLASSIC RE-ISSUE",
        img: "https://images.unsplash.com/photo-1617480173576-ceba9f423ab6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNvbmNyZXRlJTIwd2FsbCUyMHN0cmVldHdlYXIlMjB0ZXh0dXJlJTIwbmlnaHQlMjBiYXNrZXRiYWxsJTIwc3RyZWV0JTIwZm9vdGJhbGx8ZW58MHx8fGJsYWNrfDE3OTMyMTU2OTF8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "basquete",
        title: "BASQUETE",
        tag: "03 // HARDCOURT",
        img: "https://images.unsplash.com/photo-1544919982-b61976f0ba43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHx1cmJhbiUyMGNvbmNyZXRlJTIwd2FsbCUyMHN0cmVldHdlYXIlMjB0ZXh0dXJlJTIwbmlnaHQlMjBiYXNrZXRiYWxsJTIwc3RyZWV0JTIwZm9vdGJhbGx8ZW58MHx8fGJsYWNrfDE3OTMyMTU2OTF8MA&ixlib=rb-4.1.0&q=85",
    },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ onSelect }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
            <motion.div style={{ y }} className="absolute inset-0">
                <img src={BG} alt="" className="h-[120%] w-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/70 via-[#0B0B0B]/85 to-[#0B0B0B]" />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-36 pb-16 sm:pb-24">
                <motion.p
                    data-testid="hero-eyebrow"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.6 }}
                    className="mono-label"
                >
                    Cultura urbana // Esporte de rua — Brasil
                </motion.p>

                <h1
                    data-testid="hero-title"
                    className="mt-6 font-display font-extrabold uppercase leading-[0.88] tracking-tighter"
                >
                    <span className="block overflow-hidden">
                        <motion.span
                            className="block text-[clamp(1.85rem,8vw,6.5rem)] whitespace-nowrap"
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
                        >
                            Campo
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden">
                        <motion.span
                            className="block text-[clamp(1.85rem,8vw,6.5rem)] whitespace-nowrap"
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.42 }}
                        >
                            <span className="text-[#FF6A00]">&amp;</span> Concreto
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    data-testid="hero-tagline"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.7 }}
                    className="mt-6 font-condensed text-lg sm:text-2xl font-semibold uppercase tracking-[0.3em] text-[#A1A1AA]"
                >
                    Futebol <span className="text-[#FF6A00]">•</span> Retrô{" "}
                    <span className="text-[#FF6A00]">•</span> Basquete
                </motion.p>

                <div className="mt-14 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
                    {CARDS.map((card, i) => (
                        <motion.button
                            key={card.id}
                            data-testid={`category-card-${card.id}`}
                            onClick={() => onSelect(card.id)}
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.65 + i * 0.12, duration: 0.7, ease: EASE }}
                            className="group relative h-44 sm:h-60 overflow-hidden border border-[#27272A] hover:border-[#FF6A00] transition-colors duration-300 text-left"
                        >
                            <img
                                src={card.img}
                                alt={card.title}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:opacity-70 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                            <div className="relative h-full flex flex-col justify-between p-5">
                                <span className="mono-label">{card.tag}</span>
                                <div className="flex items-end justify-between gap-2">
                                    <span className="font-condensed font-extrabold uppercase text-3xl md:text-4xl lg:text-5xl tracking-wide">
                                        {card.title}
                                    </span>
                                    <ArrowUpRight
                                        size={28}
                                        className="shrink-0 text-[#FF6A00] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                    />
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>
        </section>
    );
};
